import type { Accent } from "./api/types";

/**
 * Wanted accent palette for a business page (design doc 1.3: accent is a name, never a hex).
 * The background is `--accent-foreground-{name}`; the text on top is picked per theme so the
 * initial stays at WCAG AA (4.5:1 or better). Ratios were measured against the token values in
 * src/styles/wanted/tokens/fig-tokens.css and are re-checked in accent.test.ts.
 */
export interface AccentSpec {
  name: Accent;
  label: string;
  background: string;
  onLight: "var(--static-white)" | "var(--static-black)";
  onDark: "var(--static-white)" | "var(--static-black)";
}

const WHITE = "var(--static-white)" as const;
const BLACK = "var(--static-black)" as const;

export const ACCENTS: readonly AccentSpec[] = [
  {
    name: "blue",
    label: "파랑",
    background: "var(--accent-foreground-blue)",
    onLight: WHITE,
    onDark: BLACK,
  },
  {
    name: "violet",
    label: "보라",
    background: "var(--accent-foreground-violet)",
    onLight: WHITE,
    onDark: BLACK,
  },
  {
    name: "purple",
    label: "자주",
    background: "var(--accent-foreground-purple)",
    onLight: WHITE,
    onDark: BLACK,
  },
  {
    name: "pink",
    label: "분홍",
    background: "var(--accent-foreground-pink)",
    onLight: BLACK,
    onDark: BLACK,
  },
  {
    name: "red",
    label: "빨강",
    background: "var(--accent-foreground-red)",
    onLight: WHITE,
    onDark: BLACK,
  },
  {
    name: "red-orange",
    label: "다홍",
    background: "var(--accent-foreground-red-orange)",
    onLight: BLACK,
    onDark: BLACK,
  },
  {
    name: "orange",
    label: "주황",
    background: "var(--accent-foreground-orange)",
    onLight: BLACK,
    onDark: BLACK,
  },
  {
    name: "green",
    label: "초록",
    background: "var(--accent-foreground-green)",
    onLight: BLACK,
    onDark: BLACK,
  },
  {
    name: "cyan",
    label: "청록",
    background: "var(--accent-foreground-cyan)",
    onLight: BLACK,
    onDark: BLACK,
  },
];

export const DEFAULT_ACCENT: Accent = "blue";

export function accentSpec(name: string | null | undefined): AccentSpec {
  return ACCENTS.find((a) => a.name === name) ?? (ACCENTS[0] as AccentSpec);
}

/**
 * Inline style for `.tv-accent` (globals.css): background plus the light/dark text tokens.
 * Custom properties keep the theme switch in CSS, so no re-render is needed.
 */
export function accentStyle(name: string | null | undefined): Record<string, string> {
  const a = accentSpec(name);
  return {
    "--tv-accent-bg": a.background,
    "--tv-accent-on-light": a.onLight,
    "--tv-accent-on-dark": a.onDark,
  };
}
